import React, { FC } from "react";

import { t } from "i18next";
import { useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import UserService from "@/services/UserService";

import { PageLayout } from "@/components/templates";

import { meKeys } from "@/constants/CQueryKeys";

import { AccountTab } from "./tabs/AccountTab";
import { ProfileTab } from "./tabs/ProfileTab";
import { SecurityTab } from "./tabs/SecurityTab";

const TABS = ["profile", "security", "account"] as const;
type TTab = (typeof TABS)[number];

const isTab = (value: string | null): value is TTab =>
	TABS.includes(value as TTab);

/**
 * /profile, in three tabs (`?tab=`, so a mail can link straight to one):
 * who I am and how I work, how I sign in, my account and what it costs.
 * Each tab is its own file under tabs/; this page loads `/me` once and
 * hands it to the one shown.
 */
export const Profile: FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const tabParam = searchParams.get("tab");
	const tab: TTab = isTab(tabParam) ? tabParam : "profile";

	const { isLoading, error, data } = useQuery({
		queryKey: meKeys.all,
		queryFn: () => UserService.fetchMe(),
		staleTime: 30_000,
	});

	return (
		<PageLayout
			title={t("account.title")}
			isLoading={isLoading}
			error={!!error}
			tabs={TABS.map((value) => ({
				value,
				label: t(`account.tabs.${value}`),
			}))}
			selectedTabId={tab}
			onSelectTab={(value) => {
				const next = new URLSearchParams(searchParams);
				if (value === "profile") next.delete("tab");
				else next.set("tab", value);
				setSearchParams(next, { replace: true });
			}}
		>
			{!!data && tab === "profile" && <ProfileTab me={data} />}
			{!!data && tab === "security" && <SecurityTab me={data} />}
			{!!data && tab === "account" && <AccountTab me={data} />}
		</PageLayout>
	);
};
